import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import routes from "@/config/routes.ts";
import DiplomaRequestMobileLayout from "@/layouts/DiplomaRequestMobileLayout.tsx";
import CustomDatePicker from "@edusogno/composites/calendar";
import dayjs, { Dayjs } from "dayjs";
import useDiplomaRequest from "@/utils/hooks/useDiplomaRequest.ts";
import { useCalendarList } from "@/data/diploma.ts";
import { getInterval } from "@/utils/diploma.ts";
import { SpinnerLoader } from "@/components/ui/Loader.tsx";
import { useTranslation } from "react-i18next";
import Button from "@edusogno/primitives/button";
import { useMe } from "@/data/user";

export default function DiplomaRequestSelectDate() {
  const navigate = useNavigate();
  const { date: stateDate, setState } = useDiplomaRequest();
  const { t, i18n } = useTranslation();
  const { me } = useMe();
  const user = me?.item.user;

  const { calendar, isLoading: loadingEvents } = useCalendarList({
    default: true,
    interval: getInterval(),
    parse: true,
    isUpselling: true,
    token: user?.token,
  });

  const { selectedDayEvents, availableDays } = useMemo(() => {
    const selectedDays = calendar?.filter(
      (e) =>
        dayjs(e.date).isSame(stateDate, "day") &&
        !dayjs(e.date).isBefore(dayjs()),
    );
    const daysAvailable: Dayjs[] = [];
    calendar?.forEach((el) => {
      const date = dayjs(el.date);
      if (!daysAvailable.includes(date) && !dayjs(date).isBefore(dayjs())) {
        daysAvailable.push(date);
      }
    });

    return {
      selectedDayEvents: selectedDays,
      availableDays: daysAvailable,
    };
  }, [calendar, stateDate]);

  return (
    <DiplomaRequestMobileLayout>
      {loadingEvents ? (
        <div className="flex items-center justify-center w-full h-full">
          <SpinnerLoader className={"!h-10 !w-10"} />
        </div>
      ) : (
        availableDays && (
          <>
            <div className={"h-full w-full flex-grow gap-x-4"}>
              <CustomDatePicker
                openDates={availableDays}
                lang={i18n.language as "en" | "it" | "es" | "fr" | "de"}
                onValueChange={(value) => {
                  if (value) {
                    setState("date", value as string);
                    setState("hour", undefined);
                  }
                }}
                minDate={dayjs()}
                value={stateDate!}
                type={"single"}
                className={"w-full h-full max-h-80"}
              />
            </div>
            <div className={"flex justify-between gap-x-2 w-full"}>
              <Button
                variant={"contained"}
                size={"md"}
                disabled={!stateDate || !selectedDayEvents?.length}
                onClick={() => navigate(-1)}
              >
                {t("btns.back")}
              </Button>
              <Button
                variant={"contained"}
                size={"md"}
                color={"green"}
                disabled={!stateDate || !selectedDayEvents?.length}
                onClick={() =>
                  navigate(routes.diploma_request_select_hour, {
                    state: selectedDayEvents,
                  })
                }
              >
                {t("btns.confirm")}
              </Button>
            </div>
          </>
        )
      )}
    </DiplomaRequestMobileLayout>
  );
}
